import { OpenAPI } from 'api/schema/core/OpenAPI'
import { request as __request } from 'api/schema/core/request'
import {
  CleaningZone,
  CleaningZoneRead,
  EventStatistics,
  FileData,
  MainService,
  Mission,
  MissionJob,
  MissionJobTypeEnum,
  MissionPointTransferRouteRead,
  MissionRead,
  MissionReadList,
  MissionStatus,
  MissionXyzLayerRead,
  Odyssey,
  RestrictZone,
  SchedulerSettings,
  TransferRouteRead,
} from 'api/schema/index'
import { LineString, MultiLineString, Point, Polygon } from 'geojson'
import chunk from 'lodash.chunk'
import {
  DEFAULT_VIZUALIZATION_SETTINGS,
  GEOTIFF_LAYER_STATUSES,
  MISSION_CALCULATING_STATUSES,
  MISSION_JOB_STATUSES,
  MISSION_ZONE_TYPES,
  TRANSFER_CALCULATING_STATUSES,
  TRANSFER_TYPE,
} from 'utils/constants'
import {
  ICleaningZone,
  IGeoTiffLayer,
  ILaunchPoint,
  IMission,
  IMissionInfo,
  IPoint,
  IRecordedRoute,
  IRestrictZone,
  IRoute,
  ITransferRoute,
  IVisualizationSettings,
  IZone,
  IZoneTransferRoute,
} from 'utils/interfaces'

import { RecordedRouteRead } from './schema/models/RecordedRouteRead'

const normalizeCleaningZone = (zoneRaw: CleaningZoneRead | CleaningZone): ICleaningZone => {
  return {
    id: 'cleaning_' + zoneRaw.id,
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: (zoneRaw.geometry as Polygon).coordinates,
    },
    properties: {
      zone_type: zoneRaw.is_recorded ? MISSION_ZONE_TYPES.RECORDED : MISSION_ZONE_TYPES.CLEANING,
      name: zoneRaw.name || '',
      description: zoneRaw.description || '',
      zone_id: zoneRaw.id,
      cleaning_type: zoneRaw.cleaning_type ?? 1,
      id_on_map: 'cleaning_' + zoneRaw.id,
      status: (zoneRaw as CleaningZoneRead).status
        ? ((zoneRaw as CleaningZoneRead).status as MISSION_CALCULATING_STATUSES)
        : MISSION_CALCULATING_STATUSES.NEW,
      readyForCalculation: (zoneRaw as CleaningZoneRead).is_ready_to_start_calculation
        ? (zoneRaw as CleaningZoneRead).is_ready_to_start_calculation
        : true,
      length: (zoneRaw as CleaningZoneRead).cleaning_route_geometry_length || undefined,
      area: (zoneRaw as CleaningZoneRead).geometry_area || undefined,
      executionTime: (zoneRaw as CleaningZoneRead).cleaning_route_execution_time || undefined,
      route_id: (zoneRaw as CleaningZoneRead).route_id || undefined,
      is_recorded: (zoneRaw as CleaningZoneRead).is_recorded || undefined,
      is_border_cleaning: (zoneRaw as CleaningZoneRead).is_border_cleaning || undefined,
      createdAt: (zoneRaw as CleaningZoneRead).created_at,
      updatedAt: (zoneRaw as CleaningZoneRead).updated_at,
      cleaning_speed:
        zoneRaw.cleaning_speed !== undefined && zoneRaw.cleaning_speed !== null ? zoneRaw.cleaning_speed : 0.5,
      transport_speed:
        zoneRaw.transport_speed !== undefined && zoneRaw.transport_speed !== null ? zoneRaw.transport_speed : 0.7,
      equipment_setup: (zoneRaw as any).equipment_setup || undefined,
    },
  }
}

const normalizeRecordedRoute = (routeRaw: RecordedRouteRead | any): IRecordedRoute => {
  const geometry = routeRaw.cleaning_route_geometry || routeRaw.route_geometry
  return {
    id: routeRaw.id,
    type: 'Feature',
    geometry: {
      type: 'MultiLineString',
      coordinates: geometry?.coordinates ? chunk(geometry.coordinates[0], 30000) : [],
    },
    properties: {
      name: routeRaw.name || (routeRaw.filename ? routeRaw.filename.replace('.json', '') : '') || '',
      zone_id: routeRaw.id,
      id_on_map: 'recorded_route_' + routeRaw.id,
      length: routeRaw.route_length || undefined,
      filename: routeRaw.filename || undefined,
      createdAt: routeRaw.created_at,
      updatedAt: routeRaw.updated_at,
      recordedAt: routeRaw.recorded_at || undefined,
      userId: routeRaw.user_id,
      groupId: routeRaw.group_id,
    },
  }
}

const normalizeRestrictZone = (zoneRaw: RestrictZone): IRestrictZone => {
  return {
    id: 'restrict_' + zoneRaw.id,
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: (zoneRaw.geometry as Polygon).coordinates,
    },
    properties: {
      zone_type: MISSION_ZONE_TYPES.RESTRICT,
      name: zoneRaw.name || '',
      description: zoneRaw.description || '',
      zone_id: zoneRaw.id,
      id_on_map: 'restrict_' + zoneRaw.id,
    },
  }
}

const normalizeCleaningRoute = (cleaningZoneRaw: CleaningZoneRead | any): IRoute => {
  const geometry = cleaningZoneRaw.cleaning_route_geometry || cleaningZoneRaw.route_geometry
  return {
    id: 'cleaning_route_' + cleaningZoneRaw.id,
    type: 'Feature',
    geometry: {
      type: 'MultiLineString',
      coordinates: geometry?.coordinates ? chunk(geometry.coordinates[0], 30000) : [],
    },
    properties: {
      zone_id: cleaningZoneRaw.id,
      id_on_map: 'cleaning_' + cleaningZoneRaw.id,
      name:
        cleaningZoneRaw.name || (cleaningZoneRaw.filename ? cleaningZoneRaw.filename.replace('.json', '') : '') || '',
    },
  }
}

const normalizeTransferRoute = (transferRouteRaw: TransferRouteRead, transportSpeed: number): IZoneTransferRoute => {
  const length = transferRouteRaw.geometry_length || transferRouteRaw.user_geometry_length || undefined
  const executionTime = length && transportSpeed > 0 ? length / transportSpeed : undefined

  return {
    id: 'transfer_route_' + transferRouteRaw.id,
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: transferRouteRaw.geometry?.coordinates || [],
    },
    userGeometry: {
      type: 'LineString',
      coordinates: transferRouteRaw.user_geometry?.coordinates || transferRouteRaw.geometry?.coordinates || [],
    },
    properties: {
      id: transferRouteRaw.id,
      zone_id_from: transferRouteRaw.cleaning_zone_from_id,
      zone_id_to: transferRouteRaw.cleaning_zone_to_id,
      id_on_map: 'transfer_route_' + transferRouteRaw.id,
      type: TRANSFER_TYPE.MIDDLE,
      status: transferRouteRaw.status
        ? (transferRouteRaw.status as TRANSFER_CALCULATING_STATUSES)
        : TRANSFER_CALCULATING_STATUSES.NEW,
      length,
      executionTime,
    },
  }
}

const normalizeRoute = (transferRouteRaw: MissionPointTransferRouteRead, transportSpeed: number): ITransferRoute => {
  const length = transferRouteRaw.geometry_length || transferRouteRaw.user_geometry_length || undefined
  const executionTime = length && transportSpeed > 0 ? length / transportSpeed : undefined

  return {
    id: 'mission_route_' + transferRouteRaw.id,
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: transferRouteRaw.geometry?.coordinates || transferRouteRaw.user_geometry?.coordinates || [],
    },
    userGeometry: {
      type: 'LineString',
      coordinates: transferRouteRaw.user_geometry?.coordinates || transferRouteRaw.geometry?.coordinates || [],
    },
    properties: {
      id: transferRouteRaw.id,
      type: transferRouteRaw.point_type === 'start' ? TRANSFER_TYPE.START : TRANSFER_TYPE.FINISH,
      status: transferRouteRaw.status
        ? (transferRouteRaw.status as TRANSFER_CALCULATING_STATUSES)
        : TRANSFER_CALCULATING_STATUSES.NEW,
      length,
      executionTime,
    },
  }
}

const normalizeLaunchPoints = (cleaningZonesRaw: CleaningZoneRead[]): ILaunchPoint[] => {
  const result = cleaningZonesRaw.filter(zone => zone.starting_point).map(zone => normalizeLaunchPoint(zone))
  return result as ILaunchPoint[]
}

const normalizeLaunchPoint = (cleaningZoneRaw: CleaningZoneRead | CleaningZone): ILaunchPoint | undefined => {
  if (cleaningZoneRaw.starting_point?.coordinates) {
    return {
      id: 'launch_point_' + cleaningZoneRaw.id,
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: cleaningZoneRaw.starting_point?.coordinates,
      },
      properties: {
        zone_id: cleaningZoneRaw.id,
        id_on_map: 'cleaning_' + cleaningZoneRaw.id,
      },
    }
  }
}

const normalizeMissionInfo = (missionRaw: MissionRead | MissionReadList): IMissionInfo => {
  const isList = 'results' in missionRaw
  const mission = isList ? missionRaw.results[0] : missionRaw

  return {
    id: mission.id,
    name: mission.name || '',
    location: mission.location || null,
    description: mission.description || '',
    createdAt: mission.created_at,
    updatedAt: mission.updated_at,
    status: mission.status as MISSION_CALCULATING_STATUSES,
    robot_type: mission.robot_type || '',
    transport_speed: mission.transport_speed || 0,
    cleaning_speed: mission.cleaning_speed || 0,
    overall_execution_time: (mission as any).overall_execution_time,
    overall_length: (mission as any).overall_length,
    overall_cleaned_area: (mission as any).overall_cleaned_area,
  }
}

const normalizeGeoTiffLayer = (layerRaw: MissionXyzLayerRead | undefined): IGeoTiffLayer | undefined => {
  if (!layerRaw) {
    return
  }
  const bbox: Polygon | undefined =
    layerRaw.bbox && layerRaw.bbox.coordinates
      ? {
          type: 'Polygon',
          coordinates: layerRaw.bbox.coordinates,
        }
      : undefined
  const geoTiffLayer = layerRaw
    ? {
        id: layerRaw.id,
        bbox,
        urlTiles: layerRaw.file_tiles || undefined,
        name: layerRaw.name || '',
        status: (layerRaw.status as GEOTIFF_LAYER_STATUSES) || GEOTIFF_LAYER_STATUSES.NEW,
      }
    : undefined
  return geoTiffLayer
}

const normalizePoint = (
  pointRaw?: { type?: 'Point'; coordinates?: Array<number> } | null,
  start: boolean = true
): IPoint | undefined => {
  if (pointRaw && pointRaw.coordinates) {
    const id = start ? 'mission_start_point' : 'mission_finish_point'
    return {
      id,
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: pointRaw?.coordinates,
      },
      properties: {
        id_on_map: id,
      },
    }
  }
}

let statusIsPending = false

export const MainApi = {
  fetchMissions: async (
    page?: number,
    pageSize?: number,
    robotType?: string,
    search?: string,
    groupId?: number
  ): Promise<{ results: IMissionInfo[]; count: number }> => {
    const missions = await MainService.mainMissionList(['-id'], page, pageSize, search, robotType, groupId)
    if (!missions.results) {
      return { results: [], count: 0 }
    }
    const normalizedMissions = missions.results.map(missionRaw => normalizeMissionInfo(missionRaw))
    return { results: normalizedMissions, count: missions.total || 0 }
  },
  createMission: async (name: string, robot_type: string, description: string): Promise<Mission> => {
    const mission = await MainService.mainMissionCreate({
      name,
      robot_type,
      description,
    })
    return mission
  },
  fetchMission: async (id: number): Promise<IMission> => {
    const missionRaw = await MainService.mainMissionRetrieve(id)
    const mission = normalizeMissionInfo(missionRaw)
    const status = missionRaw.status
      ? (missionRaw.status as MISSION_CALCULATING_STATUSES)
      : MISSION_CALCULATING_STATUSES.NEW
    const isReadyForCalculation = missionRaw.is_ready_to_start_calculation

    const cleaningZones = missionRaw.cleaning_zones.map(zoneRaw => normalizeCleaningZone(zoneRaw))
    const restrictZones = missionRaw.restrict_zones.map(zoneRaw => normalizeRestrictZone(zoneRaw))
    const cleaningRoutes = missionRaw.cleaning_zones
      .filter(zoneRaw => zoneRaw.cleaning_route_geometry)
      .map(zoneRaw => normalizeCleaningRoute(zoneRaw))
    const transferRoutes = missionRaw.transfer_routes.map(transferRouteRaw =>
      normalizeTransferRoute(transferRouteRaw, missionRaw.transport_speed || 0.7)
    )
    const launchPoints = normalizeLaunchPoints(missionRaw.cleaning_zones)
    const geoTiffLayer = normalizeGeoTiffLayer(missionRaw.xyz_layers[0])
    const startingPoint = normalizePoint(missionRaw.starting_point, true)
    const finishingPoint = normalizePoint(missionRaw.finishing_point, false)
    const pointRoutes = missionRaw.points_transfer_routes.map(routeRaw =>
      normalizeRoute(routeRaw, missionRaw.transport_speed || 0.7)
    )
    const visualizationSettings = { ...DEFAULT_VIZUALIZATION_SETTINGS, ...missionRaw.visualization_settings }
    return {
      status,
      mission,
      cleaningZones,
      restrictZones,
      cleaningRoutes,
      launchPoints,
      isReadyForCalculation,
      transferRoutes,
      geoTiffLayer,
      startingPoint,
      finishingPoint,
      pointRoutes,
      visualizationSettings,
    }
  },
  changeMissionName: async (id: number, name: string): Promise<Mission> => {
    const missionRaw = await MainService.mainMissionPartialUpdate(id, {
      name,
    })
    return missionRaw
  },

  changeLocationName: async (id: number, location: string | null): Promise<Mission> => {
    const missionRaw = await MainService.mainMissionPartialUpdate(id, {
      location,
    })
    return missionRaw
  },

  changeMissionRobotType: async (id: number, robot_type: string): Promise<Mission> => {
    const missionRaw = await MainService.mainMissionPartialUpdate(id, {
      robot_type,
    })
    return missionRaw
  },

  changeMissionSpeed: async (id: number, transport_speed: number, cleaning_speed: number): Promise<MissionRead> => {
    // First update the mission speeds
    await MainService.mainMissionPartialUpdate(id, {
      transport_speed: transport_speed,
      cleaning_speed: cleaning_speed,
    })

    // Then retrieve the full mission with updated zones
    const missionRaw = await MainService.mainMissionRetrieve(id)
    return missionRaw
  },

  changeMissionVisualSettings: async (id: number, visualSettings: IVisualizationSettings): Promise<Mission> => {
    const missionRaw = await MainService.mainMissionPartialUpdate(id, {
      visualization_settings: visualSettings,
    })
    return missionRaw
  },
  changeMissionStartPoint: async (id: number, startingPoint: Point | null): Promise<IPoint | undefined> => {
    const missionRaw = await MainService.mainMissionPartialUpdate(id, {
      starting_point: startingPoint,
    })
    return normalizePoint(missionRaw.starting_point)
  },
  changeMissionFinishPoint: async (id: number, finishingPoint: Point | null): Promise<IPoint | undefined> => {
    const missionRaw = await MainService.mainMissionPartialUpdate(id, {
      finishing_point: finishingPoint,
    })
    return normalizePoint(missionRaw.finishing_point)
  },

  updateMissionJobSchedule: async (id: number, scheduler_settings: SchedulerSettings): Promise<MissionJob> => {
    const missionJobRaw = await MainService.mainMissionJobPartialUpdate(id, {
      scheduler_settings: scheduler_settings,
      status: MISSION_JOB_STATUSES.NEW,
    })
    return missionJobRaw
  },

  updateOdysseySchedule: async (id: number, scheduler_settings: SchedulerSettings): Promise<Odyssey> => {
    const OdysseyRaw = await MainService.mainOdysseyPartialUpdate(id, {
      scheduler_settings: scheduler_settings,
      status: MISSION_JOB_STATUSES.NEW,
    })
    return OdysseyRaw
  },

  updateMissionJobType: async (id: number, type: MissionJobTypeEnum): Promise<MissionJob> => {
    const missionJobRaw = await MainService.mainMissionJobPartialUpdate(id, {
      job_type: type,
    })
    return missionJobRaw
  },

  deleteMission: async (id: number): Promise<void> => {
    await MainService.mainMissionDestroy(id)
  },

  copyMission: async (id: number): Promise<void> => {
    await MainService.mainMissionCopy(id)
  },

  createRestrictZone: async (
    missionId: number,
    name: string,
    description: string,
    geometry: Polygon
  ): Promise<IZone> => {
    const zone = await MainService.mainRestrictZoneCreate({
      name,
      description,
      mission_id: missionId,
      geometry,
    })
    return normalizeRestrictZone(zone)
  },
  deleteRestrictZone: async (id: number): Promise<void> => {
    await MainService.mainRestrictZoneDestroy(id)
  },
  patchRestrictZone: async (
    zoneId: number,
    missionId: number,
    name: string,
    description: string,
    geometry?: Polygon
  ): Promise<IZone> => {
    const zone = await MainService.mainRestrictZonePartialUpdate(zoneId, {
      name,
      description,
      mission_id: missionId,
      geometry,
    })
    return normalizeRestrictZone(zone)
  },
  createCleaningZone: async (
    missionId: number,
    name: string,
    description: string,
    geometry: Polygon,
    routeID: number | undefined,
    launchPointGeometry?: Point,
    cleaningSpeed?: number,
    transportSpeed?: number,
    is_border_cleaning?: boolean,
    zoneRoute?: MultiLineString | undefined,
    equipment_setup?: { turbine_speed?: number; front_dust_suppression?: boolean },
    is_recorded?: boolean | false
  ): Promise<{ zone: ICleaningZone; launchPoint: ILaunchPoint | undefined }> => {
    const zone = await MainService.mainCleaningZoneCreate({
      name,
      description,
      mission_id: missionId,
      geometry,
      starting_point: launchPointGeometry,
      route_id: routeID,
      cleaning_speed: cleaningSpeed,
      transport_speed: transportSpeed,
      is_border_cleaning: is_border_cleaning,
      cleaning_route_geometry: zoneRoute,
      equipment_setup: equipment_setup,
      is_recorded: is_recorded,
    })
    return { zone: normalizeCleaningZone(zone), launchPoint: normalizeLaunchPoint(zone) }
  },

  changeCleaningTypeCleaningZone: async (id: number, cleaning_type: number): Promise<CleaningZone> => {
    const zoneRaw = await MainService.mainCleaningZoneCleaningTypeUpdate(id, {
      cleaning_type,
    })
    return zoneRaw
  },

  changeCleaningZoneSpeed: async (
    id: number,
    cleaning_speed: number,
    transport_speed: number
  ): Promise<CleaningZone> => {
    const zoneRaw = await MainService.mainCleaningZonePartialUpdate(id, {
      cleaning_speed,
      transport_speed,
    })
    return zoneRaw
  },

  getRecordedRoute: async (filename: string): Promise<MultiLineString> => {
    const routeRaw = await MainService.mainGetRouteFromFile(filename)
    return routeRaw
  },

  patchCleaningZone: async (
    zoneId: number,
    missionId: number,
    name: string,
    description: string,
    routeID: number | undefined,
    geometry?: Polygon,
    cleaning_route_geometry?: MultiLineString,
    launchPoint?: Point | null,
    cleaningSpeed?: number,
    transportSpeed?: number,
    is_border_cleaning?: boolean,
    equipment_setup?: { turbine_speed?: number; front_dust_suppression?: boolean }
  ): Promise<{ zone: IZone; launchPoint: ILaunchPoint | undefined; cleaning_route_geometry: IRoute | undefined }> => {
    const updateData: any = {
      name,
      description,
      route_id: routeID,
      mission_id: missionId,
      geometry,
      cleaning_route_geometry,
      starting_point: launchPoint,
      is_border_cleaning,
    }

    if (cleaningSpeed !== undefined) {
      updateData.cleaning_speed = cleaningSpeed
    }
    if (transportSpeed !== undefined) {
      updateData.transport_speed = transportSpeed
    }
    if (equipment_setup !== undefined) {
      updateData.equipment_setup = equipment_setup
    }

    const zone = await MainService.mainCleaningZonePartialUpdate(zoneId, updateData)

    return {
      zone: normalizeCleaningZone(zone),
      launchPoint: normalizeLaunchPoint(zone),
      cleaning_route_geometry: normalizeCleaningRoute(zone),
    }
  },
  fetchCleaningZone: async (zoneId: number) => {
    const zone = await MainService.mainCleaningZoneRetrieve(zoneId)
    return { zone: normalizeCleaningZone(zone), route: normalizeCleaningRoute(zone) }
  },
  deleteCleaningZone: async (id: number): Promise<void> => {
    await MainService.mainCleaningZoneDestroy(id)
  },
  copyCleaningZoneToMission: async (id: number, missionId: number): Promise<void> => {
    await MainService.mainCleaningZoneCopy(id, missionId)
  },

  moveZone: async (id: number, up: boolean): Promise<void> => {
    await MainService.mainMoveZone(id, up)
  },

  startCalculatingRoutes: async (missionId: number): Promise<void> => {
    if (statusIsPending) {
      statusIsPending = false
    }
    await MainService.mainMissionStartCalculationCreate(missionId)
  },
  fetchCalculationStatus: async (missionId: number): Promise<MissionStatus | undefined> => {
    if (statusIsPending) {
      return
    }
    statusIsPending = true
    const status = await MainService.mainMissionGetStatusRetrieve(missionId)
    if (!statusIsPending) {
      return
    }
    statusIsPending = false
    return status
  },
  startCalculatingRoute: async (zoneId: number): Promise<void> => {
    if (statusIsPending) {
      statusIsPending = false
    }
    await MainService.mainCleaningZoneStartCalculationCreate(zoneId)
  },
  startCalculatingTransferRoute: async (transferRouteId: number): Promise<void> => {
    if (statusIsPending) {
      statusIsPending = false
    }
    await MainService.mainTransferRouteStartCalculationCreate(transferRouteId)
  },
  startCalculatingMissionPointRoute: async (transferRouteId: number): Promise<void> => {
    if (statusIsPending) {
      statusIsPending = false
    }
    await MainService.mainMissionPointTransferRouteStartCalculationCreate(transferRouteId)
  },
  patchTransferRoute: async (id: number, geometry?: LineString): Promise<void> => {
    await MainService.mainTransferRoutePartialUpdate(id, {
      user_geometry: geometry,
    })
  },
  patchTransferRouteWithRecorded: async (id: number, routeId: number): Promise<void> => {
    await __request(OpenAPI, {
      method: 'PATCH',
      url: `/api/main/transfer-route/${id}/`,
      body: {
        route_id: routeId,
      },
    })
  },
  deleteTransferRoute: async (id: number): Promise<void> => {
    await MainService.mainTransferRouteDestroy(id)
  },
  patchMissionPointTransferRoute: async (id: number, geometry?: LineString): Promise<void> => {
    await MainService.mainMissionPointTransferRoutePartialUpdate(id, {
      user_geometry: geometry,
    })
  },
  patchMissionPointTransferRouteWithRecorded: async (id: number, routeId: number): Promise<void> => {
    await __request(OpenAPI, {
      method: 'PATCH',
      url: `/api/main/mission-point-transfer-route/${id}/`,
      body: {
        route_id: routeId,
      },
    })
  },
  deleteMissionPointTransferRoute: async (id: number): Promise<void> => {
    await MainService.mainMissionPointTransferRouteDestroy(id)
  },
  createLayer: async (missionId: number, name: string): Promise<IGeoTiffLayer> => {
    const layer = await MainService.mainMissionXyzLayerCreate({ name, mission_id: missionId })
    return { id: layer.id, urlTiles: undefined, name, status: GEOTIFF_LAYER_STATUSES.NEW }
  },
  uploadLayerFile: async (layerId: number, file: File): Promise<void> => {
    await MainService.mainMissionXyzLayerUploadFileCreate(layerId, { file })
  },
  deleteLayer: async (layerId: number): Promise<void> => {
    await MainService.mainMissionXyzLayerDestroy(layerId)
  },
  async fetchGeotiffFiles(): Promise<FileData[]> {
    try {
      const response = await MainService.mainListGeoTiffFiles()
      return response
    } catch (error) {
      console.error('Error fetching GeoTIFF files:', error)
      throw error
    }
  },

  async fetchRecordedRoutesList(): Promise<IRecordedRoute[]> {
    try {
      const response = await MainService.mainListRecordedRoutes()
      return response ? response.map((routeRaw: any) => normalizeRecordedRoute(routeRaw)) : []
    } catch (error) {
      console.error('Error fetching recorded routes files:', error)
      throw error
    }
  },

  async fetchRecordedRoutes(
    page?: number,
    pageSize?: number,
    search?: string
  ): Promise<{ results: IRecordedRoute[]; count: number }> {
    try {
      const response = await MainService.mainRecordedRouteList(page, pageSize, search)
      return {
        results: response.results ? response.results.map((routeRaw: any) => normalizeRecordedRoute(routeRaw)) : [],
        count: response.total || 0,
      }
    } catch (error) {
      console.error('Error fetching recorded routes:', error)
      throw error
    }
  },
  async fetchRecordedRoute(id: number): Promise<IRecordedRoute> {
    try {
      const response = await MainService.mainRecordedRouteRetrieve(id)
      return normalizeRecordedRoute(response)
    } catch (error) {
      console.error('Error fetching recorded route:', error)
      throw error
    }
  },
  async deleteRecordedRouteItem(id: number): Promise<void> {
    try {
      await MainService.mainRecordedRouteDestroy(id)
      return
    } catch (error) {
      console.error('Error deleting recorded route:', error)
      throw error
    }
  },

  copyRecordedRoute: async (id: number): Promise<void> => {
    try {
      await __request(OpenAPI, {
        method: 'POST',
        url: '/api/main/recorded-route/{id}/copy/',
        path: {
          id: id,
        },
      })
    } catch (error) {
      console.error('Error copying recorded route:', error)
      throw error
    }
  },

  renameRouteRecordedZone: async (id: number, name: string): Promise<void> => {
    try {
      await MainService.mainRecordedRoutePartialUpdate(id, { name })
      return
    } catch (error) {
      console.error('Error renaming recorded route:', error)
      throw error
    }
  },

  updateRecordedRouteGeometry: async (id: number, geometry: MultiLineString): Promise<void> => {
    try {
      // @ts-ignore
      await MainService.mainRecordedRoutePartialUpdate(id, { route_geometry: geometry })
    } catch (error) {
      console.error('Error updating recorded route geometry:', error)
      throw error
    }
  },

  uploadLayerFileByName: async (layerId: number, filename: string): Promise<void> => {
    await MainService.mainMissionXyzLayerUploadFileByName(layerId, { filename })
  },
  fetchEventStatistics: async (params?: {
    start_date?: string
    end_date?: string
    robot_sn?: number
    mission_id?: number
    task_id?: number
    page?: number
    page_size?: number
    ordering?: string
  }) => {
    return await MainService.getEventStatistics(params)
  },
  fetchOdysseyEventStatistics: async (params?: {
    start_date?: string
    end_date?: string
    robot_sn?: number
    mission_id?: number
    task_id?: number
    page?: number
    page_size?: number
    ordering?: string
  }) => {
    return await MainService.getOdysseyEventStatistics(params)
  },
  fetchFacilities: async (
    page?: number,
    pageSize?: number,
    search?: string
  ): Promise<{ results: any[]; count: number }> => {
    const query: Record<string, any> = {}
    if (page) query.page = page
    if (pageSize) query.page_size = pageSize
    if (search) query.search = search
    query.ordering = '-id'

    const response = (await __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/facility/',
      query,
    })) as any
    return { results: response.results || [], count: response.total || 0 }
  },

  createFacility: async (data: {
    name: string
    facility_type?: string
    street_address?: string
    city?: string
    country?: string
    state_province?: string
    postal_code?: string
    robots_ids?: number[]
  }): Promise<any> => {
    return await __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/facility/',
      body: data,
    })
  },

  deleteFacility: async (id: number): Promise<void> => {
    await __request(OpenAPI, {
      method: 'DELETE',
      url: '/api/main/facility/{id}/',
      path: { id },
    })
  },

  patchFacility: async (
    id: number,
    data: {
      name?: string
      facility_type?: string
      street_address?: string
      city?: string
      country?: string
      state_province?: string
      postal_code?: string
      robots_ids?: number[]
    }
  ): Promise<any> => {
    return await __request(OpenAPI, {
      method: 'PATCH',
      url: '/api/main/facility/{id}/',
      path: { id },
      body: data,
    })
  },

  fetchFacility: async (id: number): Promise<any> => {
    return await __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/facility/{id}/',
      path: { id },
    })
  },

  fetchScenarios: async (
    page?: number,
    pageSize?: number,
    search?: string,
    robot_type?: string,
    group_id?: number
  ): Promise<{ results: any[]; count: number }> => {
    const query: Record<string, any> = {}
    if (pageSize != null) {
      query.page_size = pageSize
      query.page = page != null && page >= 1 ? page : 1
    }
    if (search) query.search = search
    if (robot_type) query.robot_type = robot_type
    if (group_id) query.group = group_id
    query.ordering = '-id'

    const response: any = await __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/scenario/',
      query,
    })
    const list = response?.results ?? (Array.isArray(response) ? response : [])
    const count = Number(response?.total ?? response?.count ?? 0) || 0
    return { results: list, count }
  },

  fetchScenario: async (id: number): Promise<any> => {
    return await __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/scenario/{id}/',
      path: { id },
    })
  },

  createScenario: async (name: string): Promise<any> => {
    return await __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/scenario/',
      body: { name },
    })
  },

  patchScenarios: async (
    id: number,
    data: { name?: string; missions_ids?: number[]; robot_type?: string; maintenance?: any }
  ): Promise<any> => {
    return await __request(OpenAPI, {
      method: 'PATCH',
      url: '/api/main/scenario/{id}/',
      path: { id },
      body: data,
    })
  },

  copyScenario: async (id: number): Promise<void> => {
    await __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/scenario/{id}/copy/',
      path: { id },
    })
  },

  deleteScenario: async (id: number): Promise<void> => {
    await __request(OpenAPI, {
      method: 'DELETE',
      url: '/api/main/scenario/{id}/',
      path: { id },
    })
  },

  fetchGausiumTaskReports: async (params: { start_date: string; end_date: string; robot_sn?: string }) => {
    const res = await MainService.getGausiumTaskReports(params)
    return res as { total: number; results: EventStatistics[]; gausium_robot_sns: string[] }
  },

  fetchDashboardNormalized: async (params: {
    start_date: string
    end_date: string
    robot_sn?: string
    source?: 'autonomics' | 'gausium_task_reports' | 'event_statistics' | 'odyssey_statistics'
    cache_only?: boolean
  }) => {
    const res = (await __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/dashboard-normalized/',
      query: params,
    })) as any
    return res as { total: number; results: any[]; timings_ms?: Record<string, number> }
  },

  /** Normalized statistics timeline for TaskSchedulerNormalized (same payload as dashboard-normalized). */
  fetchTasksNormalized: async (params: {
    start_date: string
    end_date: string
    robot_sn?: string
    source?: 'autonomics' | 'gausium_task_reports' | 'event_statistics' | 'odyssey_statistics'
    cache_only?: boolean
  }) => {
    const res = (await __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/tasks-normalized/',
      query: params,
    })) as any
    return res as { total: number; results: any[]; timings_ms?: Record<string, number> }
  },

  fetchGausiumMaps: async (
    robotIds?: number[],
    search?: string,
    page?: number,
    pageSize?: number
  ): Promise<
    { total: number; results: Array<{
      id: number
      robot_sn: string
      robot_type?: string
      robot_type_display?: string
      vendor_map_id: string
      map_version: string
      map_name: string
      subareas_json: any
      preview_url: string | null
      preview_fetched_at: string | null
      synced_at: string | null
    }> }
  > => {
    const query: Record<string, any> = {}
    if (robotIds?.length) query.robot_id = robotIds
    if (search?.trim()) query.search = search.trim()
    if (page != null && pageSize != null) {
      query.page = page
      query.page_size = pageSize
    }
    const response: any = await __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/gausium-maps/',
      query,
    })
    const results = Array.isArray(response?.results) ? response.results : []
    const total = Number(response?.total ?? response?.count ?? 0) || 0
    return {
      total,
      results,
    }
  },
}
